import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../api-config';

export interface BranchServiceShippingMethodSelectOption {
  value: string;
  label: string;
}

/**
 * Formas de envío del servicio en la sucursal (subtab Forma de envío).
 * Fuente del multiselect de Estatus.
 *
 * GET {apiUrl}/selects/branch-service-shipping-methods?branchId=&serviceId=
 */
@Injectable({ providedIn: 'root' })
export class BranchServiceShippingMethodsSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<
    string,
    BranchServiceShippingMethodSelectOption[]
  >();
  private readonly endpointUrl = '/selects/branch-service-shipping-methods';

  getOptions(
    branchId: string,
    serviceId: string,
  ): Observable<BranchServiceShippingMethodSelectOption[]> {
    const branch = branchId.trim();
    const service = serviceId.trim();
    if (!branch || !service) return of([]);

    const cacheKey = `${branch}|${service}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        params: { branchId: branch, serviceId: service },
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(cacheKey, options)),
        catchError(() => of([])),
      );
  }

  /** Invalida cache tras guardar el drawer Configurar. */
  invalidate(branchId?: string, serviceId?: string): void {
    if (!branchId?.trim()) {
      this.cache.clear();
      return;
    }
    const branch = branchId.trim();
    if (!serviceId?.trim()) {
      for (const key of [...this.cache.keys()]) {
        if (key.startsWith(`${branch}|`)) this.cache.delete(key);
      }
      return;
    }
    this.cache.delete(`${branch}|${serviceId.trim()}`);
  }

  private parseSelectResponse(
    body: unknown,
  ): BranchServiceShippingMethodSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{
          id: string;
          text?: string;
          label?: string;
          name?: string;
        }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id || '',
        label: item.text || item.label || item.name || '',
      }));
    } catch {
      return [];
    }
  }
}
